import { API } from 'aws-amplify';

/**
 * Get the current authenticated user
 * @returns {Promise<Object|null>} The current user or null if not authenticated
 */
export async function getCurrentUser() {
    try {
        const user = await API.Auth.currentAuthenticatedUser();
        const attributes = user.attributes;
        attributes.username = attributes.preferred_username || attributes['custom:legacyUsername'] || user.username; // Include username for ownership checks
        const accessGroups = user.signInUserSession?.accessToken?.payload?.["cognito:groups"] || [];
        const idGroups = user.signInUserSession?.idToken?.payload?.["cognito:groups"] || [];
        attributes.groups = [...new Set([...accessGroups, ...idGroups])];
        return attributes;
    } catch (error) {
        console.error('Authentication error:', error);
        return null;
    }
}

/**
 * Check if the current user belongs to the admin Cognito group
 * @returns {Promise<boolean>} True if the current user is an admin
 */
export async function isAdminUser() {
    const currentUser = await getCurrentUser();
    return Array.isArray(currentUser?.groups) && currentUser.groups.some(group => String(group).toLowerCase() === 'admin');
}

/**
 * Check if the current user is the owner of a video
 * @param {Object} video - The video object with owner information
 * @returns {Promise<boolean>} True if the current user is the owner
 */
export async function isVideoOwner(video) {
    if (!video?.authOwner) {
        return false;
    }

    const currentUser = await getCurrentUser();
    if (!currentUser) {
        return false;
    }

    // Match the VideoModel @auth update/delete rule exactly:
    // ownerField: authOwner, identityClaim: sub.
    return currentUser.sub === video.authOwner;
}

/**
 * Check if the current user can edit a video
 * @param {Object} video - The video object with owner information
 * @returns {Promise<boolean>} True if current user owns the video or is an admin
 */
export async function canEditVideo(video) {
    if (await isAdminUser()) {
        return true;
    }

    return isVideoOwner(video);
}

/**
 * Check if the current user is authenticated
 * @returns {Promise<boolean>} True if the user is authenticated
 */
export async function isAuthenticated() {
    try {
        await API.Auth.currentAuthenticatedUser();
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Require authentication - redirect to login if not authenticated
 * @param {Object} router - Vue router instance
 * @returns {Promise<boolean>} True if authenticated, false if redirected
 */
export async function requireAuth(router) {
    const authenticated = await isAuthenticated();
    if (!authenticated) {
        // Redirect to login or show auth modal
        if (router) {
            router.push('/');
        }
        return false;
    }
    return true;
} 
